<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { control } from "@/design/control";
import Check from "@/design/primitives/Check.vue";
import { displayOf, optionsFor } from "../resolvers/field-config";
import type { WidgetProps } from "./contract";

/**
 * Several choices, as checkboxes or a multi-select.
 *
 * The value is always an ARRAY, including when it is empty. Emitting null for
 * "nothing chosen" would make a `text[]` column null rather than `{}`, and the
 * two behave differently in every query that follows.
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const options = computed(() => optionsFor(props.field.config));
const asChecks = computed(() => displayOf(props.field.config) !== "dropdown");
const chosen = computed<string[]>(() =>
	Array.isArray(props.modelValue) ? props.modelValue.map(String) : [],
);

/**
 * O único controle que não passa pelo `SelectField`.
 *
 * Um `<select multiple>` mostra várias linhas e não tem altura de uma linha,
 * então a receita entra sem a altura — é a mesma borda, o mesmo raio e o mesmo
 * foco, só que crescendo com as opções.
 */
const listClasses = computed(() => twMerge(control(), "h-auto py-1.5"));

function toggle(value: string, on: boolean): void {
	const next = on
		? [...new Set([...chosen.value, value])]
		: chosen.value.filter((v) => v !== value);
	emit("update:modelValue", next);
}

function pickMany(event: Event): void {
	const select = event.target as HTMLSelectElement;
	emit(
		"update:modelValue",
		[...select.selectedOptions].map((o) => o.value),
	);
}
</script>

<template>
	<div
		v-if="asChecks"
		:id="controlId"
		class="space-y-1.5"
		role="group"
		:aria-label="field.label"
		:aria-describedby="describedBy"
	>
		<Check
			v-for="option in options"
			:key="option.value"
			:label="option.label"
			:model-value="chosen.includes(option.value)"
			:disabled="disabled"
			@update:model-value="toggle(option.value, $event)"
		/>
	</div>

	<select
		v-else
		:id="controlId"
		multiple
		:disabled="disabled"
		:aria-invalid="Boolean(error)"
		:aria-describedby="describedBy"
		:class="listClasses"
		@change="pickMany"
		@blur="emit('blur')"
	>
		<option
			v-for="option in options"
			:key="option.value"
			:value="option.value"
			:selected="chosen.includes(option.value)"
		>
			{{ option.label }}
		</option>
	</select>
</template>
